import { raiseBoardApi } from "@/api/raise-board-api";
import { useWorkspaceRole } from "@/hooks/use-workspace-role";
import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Copy, ExternalLink, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { toast } from "sonner";
import posthog from "posthog-js";
import { PublicCoverSettings } from "./public-cover-settings";

export default function RaiseBoardSettings() {
  const { workspace, isLoading, activeWorkspaceId, invalidateWorkspace } =
    useWorkspaceSettings();
  const { workspaceId: urlWorkspaceId } = useParams<{ workspaceId: string }>();
  const workspaceId = urlWorkspaceId || activeWorkspaceId;
  const { isWorkspaceOwner } = useWorkspaceRole();

  const [enabled, setEnabled] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  useEffect(() => {
    if (!workspace) return;
    setEnabled(workspace.raise_board_enabled || false);
  }, [workspace]);

  const slug = workspace?.portal_slug ?? "";
  const raiseBoardUrl = slug ? `${window.location.origin}/rb/${slug}` : "";
  const hasSlug = Boolean(slug);

  const handleToggle = async (checked: boolean) => {
    if (!activeWorkspaceId) return;
    if (checked && !hasSlug) {
      toast.error(
        "Set your portal URL in General Settings before enabling the Raiseboard.",
      );
      return;
    }
    setIsToggling(true);
    try {
      await raiseBoardApi.toggle(activeWorkspaceId, checked);
      setEnabled(checked);
      invalidateWorkspace();
      if (checked) {
        toast.success(`Raiseboard enabled at /rb/${slug}`);
      } else {
        toast.success("Raiseboard disabled");
      }
      posthog.capture("settings_raise_board_toggled", {
        workspace_id: activeWorkspaceId,
        enabled: checked,
      });
    } catch {
      toast.error("Failed to toggle Raiseboard. Please try again.");
    } finally {
      setIsToggling(false);
    }
  };

  const copyUrl = () => {
    if (!raiseBoardUrl) return;
    navigator.clipboard.writeText(raiseBoardUrl);
    toast.success("Copied to clipboard");
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // The settings nav hides this page from members, but the URL is still
  // reachable. Publishing the workspace to an unauthenticated address is an
  // owner decision, and the endpoint behind the switch is manager-only.
  if (!isWorkspaceOwner) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-8">
        <h1 className="text-lg font-semibold text-foreground">Raiseboard</h1>
        <div className="mt-6 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
          Only workspace owners can turn the public Raiseboard on or off. You
          can still add and remove companies from it on the{" "}
          <Link
            to={`/${workspaceId}/raise-board`}
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Raiseboard page
          </Link>
          .
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-8">
      <h1 className="text-lg font-semibold text-foreground">Raiseboard</h1>
      <p className="mt-0.5 text-sm text-muted-foreground">
        A public showcase of selected pipeline companies.
      </p>

      {/* Enable toggle + URL */}
      <section className="mt-8">
        <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
          Public Board
        </h2>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium">Raiseboard</div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                When on, your selected pipeline companies appear on a public
                board investors can browse.
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={handleToggle}
              disabled={isToggling || !hasSlug}
            />
          </div>

          {!hasSlug && (
            <div className="mt-3 rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              No portal URL set.{" "}
              <Link
                to={`/${workspaceId}/settings/general`}
                className="font-medium text-foreground underline-offset-4 hover:underline"
              >
                Set one in General Settings →
              </Link>
            </div>
          )}

          {hasSlug && enabled && (
            <div className="mt-4 border-t border-border pt-4">
              <Label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
                Public URL
              </Label>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  readOnly
                  value={raiseBoardUrl}
                  className="flex-1 bg-muted font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  onClick={copyUrl}
                  title="Copy URL"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  onClick={() => window.open(raiseBoardUrl, "_blank")}
                  title="Open in new tab"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                Uses the same slug as your application portal. Change in{" "}
                <Link
                  to={`/${workspaceId}/settings/general`}
                  className="text-foreground underline-offset-4 hover:underline"
                >
                  General Settings
                </Link>
                .
              </p>
            </div>
          )}
        </div>
      </section>

      {workspace && <PublicCoverSettings workspace={workspace} />}

      {/* Cross-page breadcrumb */}
      <div className="mt-8 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3 text-sm">
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">
            Logo, workspace name, and website
          </span>{" "}
          for your public board are configured in General Settings.
        </p>
        <Link
          to={`/${workspaceId}/settings/general`}
          className="mt-1 inline-block text-xs font-medium text-foreground underline-offset-4 hover:underline"
        >
          Open General Settings →
        </Link>
      </div>

      {/* Featured companies */}
      <section className="mt-8">
        <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
          Featured Companies
        </h2>
        <div className="rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3 text-sm">
          <p className="text-muted-foreground">
            Mark deals as featured from the Pipeline. Open any deal's menu and
            use{" "}
            <span className="font-medium text-foreground">Add to board</span>.
          </p>
          <Link
            to={`/${workspaceId}/deals`}
            className="mt-2 inline-block text-xs font-medium text-foreground underline-offset-4 hover:underline"
          >
            Open Pipeline →
          </Link>
        </div>
      </section>
    </div>
  );
}
